import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Download, Loader2, RefreshCw, Sparkles } from 'lucide-react';
import { Button } from '../../../shared/components/ui/button';
import { toast } from 'sonner';
import { BeforeAfterComparison } from './BeforeAfterComparison';

export const ResultSection = ({ result, onRegenerate }) => {
  const [isDownloading, setIsDownloading] = useState(false);

  if (!result) return null;

  const { imageUrl, originalImageUrl } = result;

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `professional-profile-picture-${Date.now()}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Profile picture downloaded successfully!');
    } catch (err) {
      console.error('Download error:', err);
      toast.error('Failed to download image. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <section className="py-12 md:py-16 bg-muted/30">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          {/* Section Header */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-success/10 text-success mb-4">
              <CheckCircle className="w-4 h-4" />
              <span className="text-sm font-medium">Profile Picture Generated Successfully</span>
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-3">
              Your Professional Profile Picture
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              A LinkedIn-ready professional headshot optimised for trust and credibility
            </p>
          </div>

          {/* Before/After Comparison */}
          {originalImageUrl && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="mb-12"
            >
              <div className="max-w-3xl mx-auto">
                {/* Comparison Title */}
                <div className="flex items-center justify-center gap-2 mb-6">
                  <Sparkles className="w-5 h-5 text-primary" />
                  <h3 className="font-display text-xl font-semibold text-foreground">
                    See the Transformation
                  </h3>
                </div>
                
                <div className="bg-card border border-border rounded-2xl p-6 shadow-xl">
                  <BeforeAfterComparison 
                    beforeImage={originalImageUrl}
                    afterImage={imageUrl}
                  />
                </div>
              </div>
            </motion.div>
          )}

          {/* Download Section */}
          <div className="max-w-md mx-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="relative bg-card border border-border rounded-2xl p-6 shadow-xl"
            >
              
              {/* Download Button */}
              <Button
                variant="primary"
                size="lg"
                className="w-full"
                onClick={handleDownload}
                disabled={isDownloading}
              >
                {isDownloading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Downloading...
                  </>
                ) : (
                  <>
                    <Download className="w-5 h-5" />
                    Download Profile Picture
                  </>
                )}
              </Button>

              {/* Regenerate Button */}
              <Button
                variant="outline"
                size="lg"
                className="w-full mt-3"
                onClick={onRegenerate}
              >
                <RefreshCw className="w-5 h-5" />
                Generate Another Photo
              </Button>
              
              {/* Tips */}
              <div className="mt-6 pt-6 border-t border-border">
                <p className="text-sm font-medium text-foreground mb-2">Next steps:</p>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Upload this image to your LinkedIn profile</li>
                  <li>• Use it across your professional platforms</li>
                  <li>• Consider using our Headline Generator for a complete profile upgrade</li>
                </ul>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
